import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { judge, points } from "@volca/ui/trades";
import { moneyChange, useReportCurrency } from "@/profitability/currency";
import { MissingNote, showValue } from "@/profitability/missing-value";
import { formatSummaryValue } from "@/profitability/utils";
import type { OverviewFigure } from "./types";

/** The change against the period before, its sign written out. */
export const formatChange = (
  { change, type }: Pick<OverviewFigure, "change" | "type">,
  currency?: string,
) => {
  if (change === null || change === 0) return "No change";
  if (type === "percent") return points(change);
  if (type === "currency") return moneyChange(change, currency);
  return `${change > 0 ? "+" : "−"}${Math.abs(change).toLocaleString()}`;
};

/** The period's headline figures in one strip, each with its change. */
export const OverviewFigures = ({
  figures,
  compared,
}: {
  figures: OverviewFigure[];
  compared: boolean;
}) => {
  const currency = useReportCurrency();
  return (
    <dl className="grid w-full min-w-0 grid-cols-2 gap-x-8 gap-y-4 @3xl:grid-cols-4">
      {figures.map((figure) => (
        <Stack key={figure.key} gap={1} className="min-w-0">
          <dt className="text-sm [overflow-wrap:anywhere] text-subtle">
            {figure.label}
          </dt>
          <Stack render={<dd />} direction="row" wrap align="baseline" gap={2}>
            <Text
              render={<span />}
              className="text-2xl font-medium [overflow-wrap:anywhere] tabular-nums"
            >
              {showValue(formatSummaryValue(figure, currency))}
            </Text>
            <MissingNote count={figure.missing} label={figure.label} />
            {compared && figure.change !== null && figure.change !== 0 && (
              <Text
                render={<span />}
                variant="caption"
                tabular
                className={cn("whitespace-nowrap", {
                  "text-success":
                    judge(figure.change, figure.better) === "good",
                  "text-danger": judge(figure.change, figure.better) === "bad",
                })}
              >
                {formatChange(figure, currency)}
              </Text>
            )}
          </Stack>
        </Stack>
      ))}
    </dl>
  );
};
